import stringOrNumberDeserializer from '@base/utils/stringOrNumberDeserializer';
import { ObjectDeserializer } from 'object-deserializer';
import { ResponseError, ResponseUserAuthData } from '@base/features/auth/model/interfaces';
import { challengeDeserializer } from '../../ladder/model/deserializers';
import { tourneyDeserializer } from '../../tournaments/model/deserializers';
import {
  AccountStatement,
  PersonalInfo,
  Referal,
  ReferalStatistics,
  Transaction,
} from './interfaces';

function questionDeserializer(o: ObjectDeserializer) {
  return {
    question: o.required('question').asNumber,
    answer: o.required('answer').asString,
  };
}

function geoDeserializer(o: ObjectDeserializer) {
  return {
    state: o.optional('state')?.asString,
    country: o.required('country').asString,
  };
}

function addressDeserializer(o: ObjectDeserializer) {
  return {
    country: o.required('country').asString,
    city: o.required('city').asString,
    zip: o.required('zip').asString,
    province: o.optional('province')?.asString,
    state: o.optional('state')?.asString,
    street: o.required('street').asString,
    country_code: o.required('country_code').asString,
  };
}

function avatarDeserializer(o: ObjectDeserializer) {
  return {
    approved: o.optional('approved')?.asNumber,
    id: o.optional('id')?.asString,
    uploaded: o.optional('uploaded')?.asString,
  };
}

function realNameDeserializer(o: ObjectDeserializer) {
  return {
    last_name: o.required('last_name').asString,
    first_name: o.required('first_name').asString,
  };
}

export function personalInfoDeserializer(o: ObjectDeserializer): PersonalInfo {
  return {
    email: o.required('email').asString,
    about: o.optional('about')?.asString,
    login_name: o.required('login_name').asString,
    question: o.optional('question')?.asObject(questionDeserializer),
    geo: o.required('geo').asObject(geoDeserializer),
    yob: o.required('yob').asNumber,
    acct_value: o.required('acct_value').asString,
    address: o.required('address').asObject(addressDeserializer),
    code: o.required('code').asNumber,
    user_id: o.required('user_id').asString,
    avatar: o.required('avatar').asObject(avatarDeserializer),
    AdvLevel: o.required('AdvLevel').asNumber,
    real_name: o.required('real_name').asObject(realNameDeserializer),
    status: o.required('status').asNumber,
    email_verified: o.required('email_verified').asString,
    nopass: o.required('nopass').asNumber,
    phone: o.optional('phone')?.asString,
    marital_status: o.optional('marital_status')?.asString,
    gender: o.optional('gender')?.asString,
    subscriber: o.required('subscriber').asString,
  };
}

function pagerDeserializer(o: ObjectDeserializer) {
  return {
    challenges: o.required('challenges').asArrayOfObjects(challengeDeserializer),
    messages: o.required('messages').asArray((val) => String(val)),
  };
}

export function responseErrorDeserializer(o: ObjectDeserializer): ResponseError {
  return {
    code: o.required('code').asNumber,
    description: o.required('descr').asString,
  };
}

const convertToString = (data: string | number): string =>
  typeof data === 'string' ? data : data.toString();

export function partialAuthDataFaceDeserializer(o: ObjectDeserializer) {
  return {
    code: o.required('code').asNumber,
    id: o.optional('id')?.asString,
    approved: o.optional('approved')?.asNumber,
    uploaded: o.optional('uploaded')?.asString,
  };
}

export function responseUserAuthDataDeserializer(o: ObjectDeserializer): ResponseUserAuthData {
  return {
    code: o.required('code').asNumber,
    name: o.optional('name')?.asString,
    wssproxy2: o.required('wssproxy2').asString,
    session: o.required('session').as((data: any) => convertToString(data)),
    proxy: o.optional('proxy')?.asString,
    AdvLevel: o.required('AdvLevel').asNumber,
    isGuestPlayer: o.required('isGuestPlayer').asNumber,
    yob: o.optional('yob')?.asNumber,
    Phone_Verified: o.required('Phone_Verified').asString,
    pager: o.required('pager').asObject(pagerDeserializer),
    tries: o.required('tries').asNumber,
    acct_holder: o.required('acct_holder').asNumber,
    host: o.required('host').asString,
    wssproxy: o.optional('wssproxy')?.asString,
    user_id: o.required('user_id').asString,
    google_play: o.optional('google_play')?.asString,
    sign_needed: o.required('sign_needed').asNumber,
    Mail_Verified: o.required('Mail_Verified').asString,
    usr: o.required('usr').asString,
    port: o.required('port').asNumber,
    acct_value: o.required('acct_value').asString,
    proxy2: o.required('proxy2').asString,
    status: o.required('status').as(stringOrNumberDeserializer),
    proxies: o.required('proxies').asArray((val) => val),
    tourneys_in_progress: o.optional('tourneys_in_progress?')?.asArray((val) => tourneyDeserializer(val)),
    wsCurrentHost: o.optional('wsCurrentHost')?.asString,
    wsCurrentPort: o.optional('wsCurrentPort')?.asNumber,
    connection_type: o.optional('connection_type')?.asString,
    presentation: o.optional('presentation')?.asString,
    newusr: o.optional('newusr')?.asString,
    guestUser: o.optional('guestUser')?.asString,
    guestToken: o.optional('guestToken')?.asString,
    descr: o.optional('descr')?.asString,
    id: o.optional('id')?.asString,
    uploaded: o.optional('uploaded')?.asString,
    allowed: o.optional('allowed')?.asNumber,
  };
}

export function referalFeeDeserializer(o: ObjectDeserializer): Referal {
  return {
    referedName: o.required('Usr').asString,
    registrationDate: o.required('TimeEstablished').asString,
    feesInTickets: o.required('Earn').asNumber,
  };
}

export function referalStatisticsDeserializer(o: ObjectDeserializer): ReferalStatistics {
  return {
    totalFees: o.required('TotalFees').asNumber,
    percent: o.required('Percent').asString,
    timeEstablished: o.required('TimeEstablished').asString,
    rows: o.required('Rows').asArrayOfObjects(referalFeeDeserializer),
  };
}

export function accountStatementDeserializer(o: ObjectDeserializer): AccountStatement {
  return {
    rows: o.required('rows').asArrayOfObjects(transactionDeserializer),
    total_minus: o.required('total_minus').asString,
    total_plus: o.required('total_plus').asString,
  };
}

export function transactionDeserializer(o: ObjectDeserializer): Transaction {
  return {
    transactionType: o.required('description').asString,
    date: o.required('date').asString,
    time: o.required('time').asString,
    tickets: o.required('amount').as(stringOrNumberDeserializer),
    balance: o.required('acct_stamp').as(stringOrNumberDeserializer),
  };
}
